/**
 * <plw-about-bands> — measures each `.plw-about__band` and stamps how far its
 * content overflows the pinnable viewport area as `--about-band-overshoot`.
 * The section's sticky rule subtracts it from the pin offset, so a band taller
 * than the viewport (short window) keeps scrolling until its BOTTOM reaches the
 * viewport bottom before pinning — every line of text passes through view —
 * instead of pinning by its top and hiding the rest under the next band.
 *
 * Bands that fit get 0 and pin exactly as before; without JS the CSS fallback
 * (`--about-band-overshoot: 0px`) is today's behavior. The property is only
 * consumed inside the desktop + no-reduced-motion sticky media query, so the
 * measurement is safe to run unconditionally.
 */
class PlwAboutBands extends HTMLElement {
  #rafId = 0;

  connectedCallback() {
    this.bands = this.querySelectorAll('.plw-about__band');
    if (!this.bands.length) return;

    this.scheduleMeasure = this.scheduleMeasure.bind(this);
    this.resizeObserver = new ResizeObserver(this.scheduleMeasure);
    for (const band of this.bands) {
      this.resizeObserver.observe(band);
    }

    // A viewport-height change doesn't resize an overflowing band (its height
    // is its content, not the 80vh minimum), so ResizeObserver alone misses
    // exactly the case that matters — listen to window resize too.
    window.addEventListener('resize', this.scheduleMeasure);
    this.measure();
  }

  disconnectedCallback() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    window.removeEventListener('resize', this.scheduleMeasure);
    if (this.#rafId) cancelAnimationFrame(this.#rafId);
  }

  /** Coalesce observer/resize bursts into one read→write pass per frame. */
  scheduleMeasure() {
    if (this.#rafId) return;
    this.#rafId = requestAnimationFrame(() => {
      this.#rafId = 0;
      this.measure();
    });
  }

  measure() {
    const viewportHeight = window.innerHeight;
    const headerOffset =
      parseFloat(getComputedStyle(this).getPropertyValue('--header-group-height')) || 62;

    for (const [index, band] of [...this.bands].entries()) {
      // The first band pins to the very top (under the transparent header);
      // the rest pin below the solid header bar.
      const pinOffset = index === 0 ? 0 : headerOffset;
      const overshoot = Math.max(0, band.offsetHeight - (viewportHeight - pinOffset));
      band.style.setProperty('--about-band-overshoot', `${overshoot}px`);
    }
  }
}

if (!customElements.get('plw-about-bands')) {
  customElements.define('plw-about-bands', PlwAboutBands);
}
